<script setup lang="ts">
import { copyToClipboard } from '@/shared/lib'

/**
 * The block of text a user is meant to take away: a token, a snippet, a referral link.
 * Clicking anywhere on it copies and toasts the result, which is the only affordance
 * there is — turn it off with `copyOnClick` for a block that is only to be read.
 *
 * What is copied is the element's own rendered text, not the `text` prop, so it survives
 * whatever the browser did to the whitespace. It also carries 8px of bottom margin.
 *
 * Editing a snippet is `CodeEditor`, not this.
 */
const props = withDefaults(
  defineProps<{
    text: string
    copyOnClick?: boolean
  }>(),
  { copyOnClick: true }
)

function copy(e: Event) {
  if (!props.copyOnClick) return

  const target = e.currentTarget as HTMLElement
  const textToCopy = target?.textContent?.trim() ?? props.text
  if (!textToCopy) return

  void copyToClipboard(textToCopy)
}
</script>

<template>
  <pre class="code-area" @click="copy">{{ text }}</pre>
</template>

<style lang="scss" scoped>
.code-area {
  display: block;
  padding: 12px 16px;
  margin: 0 0 8px;
  font-size: 14px;
  line-height: 1.4;
  word-break: break-all;
  word-wrap: break-word;
  color: var(--el-text-color-primary);
  background-color: var(--el-fill-color-light);
  border: 1px solid var(--el-border-color-lighter);
  border-radius: var(--el-border-radius-base);
  white-space: pre-wrap;
}
</style>
